/**
 * Client-side import preview: duplicate detection + field-level merge diffs
 * for the admin Import Studio's Review step.
 *
 * Framework-free CJS (like importMapping.js) so the Node test harness can
 * assert parity with the backend. These are deliberate mirrors — functions/
 * cannot import from src/ — and the harness fails if the two drift:
 * - email/phone normalization mirrors functions/Shared Functions/dataNormalization.js
 * - duplicate scoping mirrors functions/Shared Functions/duplicateDetection.js
 * - deal dedupe keys mirror buildDealDedupeKeys in functions/CRM/imports/importDeals.js
 * - merge semantics mirror functions/CRM/imports/importMerge.js
 *
 * The preview is advisory — the backend re-checks everything at import time
 * and remains the source of truth.
 */

// ---------------------------------------------------------- normalization

/** Mirror of dataNormalization.normalizeEmail (without logging). */
function normalizeEmail(email) {
  if (!email) return '';
  return String(email).toLowerCase().trim();
}

/** Mirror of dataNormalization.normalizePhone (without logging). */
function normalizePhone(phone) {
  if (!phone) return '';
  const digitsOnly = String(phone).replace(/\D/g, '');
  if (!digitsOnly) return '';
  if (digitsOnly.length === 10) return digitsOnly;
  if (digitsOnly.length === 11 && digitsOnly[0] === '1') return digitsOnly.substring(1);
  return digitsOnly;
}

// ------------------------------------------------------------- value parsing

/** Mirror of importDeals.parseCurrencyValue. */
function parseCurrencyValue(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return isNaN(value) ? null : value;
  const cleaned = String(value).replace(/[$,\s]/g, '').replace(/%$/, '');
  if (!cleaned || cleaned === '-') return null;
  const num = parseFloat(cleaned);
  return isNaN(num) ? null : num;
}

/** Mirror of importDeals.parseIntValue. */
function parseIntValue(value) {
  const num = parseCurrencyValue(value);
  return num === null ? null : Math.round(num);
}

/** Mirror of the importDeals date parsing (estimatedClosingDate / appDate). */
function parseDateValue(value) {
  if (!value) return null;
  const parsed = new Date(value);
  if (isNaN(parsed.getTime())) return null;
  return parsed.toISOString().split('T')[0];
}

// --------------------------------------------------------------- merge rules
// Mirrors of functions/CRM/imports/importMerge.js

/** A CSV value counts as "provided" only when non-empty after trimming. */
function provided(value) {
  if (value === undefined || value === null) return false;
  return String(value).trim() !== '';
}

function parseList(value) {
  if (!value) return [];
  const list = Array.isArray(value) ? value : String(value).split(',');
  return list.map(v => String(v).trim()).filter(Boolean);
}

function unionList(existing, additions) {
  const merged = Array.isArray(existing) ? [...existing] : [];
  (additions || []).forEach(v => {
    if (v && !merged.includes(v)) merged.push(v);
  });
  return merged;
}

/**
 * Mirror of contactTypeValidation.buildContactTypeResolver's resolve() with
 * an empty fallback — unknown values drop, known values map to their slug.
 * @param {Array<{value, label}>} typeRows - crm_contact_types (from settings)
 */
function buildTypeResolver(typeRows) {
  const typeByKey = new Map();
  (typeRows || []).forEach(row => {
    if (row.value) typeByKey.set(String(row.value).toLowerCase().trim(), row.value);
    if (row.label) typeByKey.set(String(row.label).toLowerCase().trim(), row.value);
  });

  const resolve = (rawTypes) => {
    let rawList = [];
    if (Array.isArray(rawTypes)) {
      rawList = rawTypes;
    } else if (typeof rawTypes === 'string' && rawTypes.trim()) {
      rawList = rawTypes.split(',');
    }
    const resolved = [];
    rawList.forEach(raw => {
      const key = String(raw).toLowerCase().trim();
      if (!key) return;
      let value = typeByKey.get(key);
      if (!value) value = typeByKey.get(key.replace(/\s+/g, '-'));
      if (value && !resolved.includes(value)) resolved.push(value);
    });
    return resolved;
  };

  return { resolve, hasConfig: typeByKey.size > 0 };
}

/**
 * Resolve ONE raw CSV contact-type value the way the backend will
 * (slug or label, case-insensitive, spaces-to-dashes fallback), or null.
 * Powers the Studio's "Match your contact types" auto-detection.
 */
function resolveContactTypeValue(raw, typeRows) {
  const key = String(raw ?? '').toLowerCase().trim();
  if (!key) return null;
  const slugKey = key.replace(/\s+/g, '-');
  for (const t of typeRows || []) {
    const value = String(t.value ?? '').toLowerCase().trim();
    const label = String(t.label ?? '').toLowerCase().trim();
    if (value === key || label === key || value === slugKey) return t.value;
  }
  return null;
}

/**
 * Unique values found in the mapped Contact Type column (cells may hold
 * comma-separated lists), with row counts and their auto-resolution against
 * the configured types. Most frequent first.
 *
 * @returns {Array<{ value: string, count: number, resolvedTo: string|null }>}
 */
function collectContactTypeValues(csvData, header, typeRows) {
  if (!header) return [];
  const counts = new Map(); // lowercased key -> { value: first-seen casing, count }
  (csvData || []).forEach(row => {
    String((row && row[header]) ?? '')
      .split(',').map(v => v.trim()).filter(Boolean)
      .forEach(v => {
        const key = v.toLowerCase();
        const entry = counts.get(key) || { value: v, count: 0 };
        entry.count += 1;
        counts.set(key, entry);
      });
  });
  return [...counts.values()]
    .map(({ value, count }) => ({ value, count, resolvedTo: resolveContactTypeValue(value, typeRows) }))
    .sort((a, b) => b.count - a.count);
}

/**
 * Rewrite each record's contactTypes through the admin's value matching so
 * the backend resolver receives real configured types. `typeValueMap` keys
 * are lowercased raw values, values are configured type slugs. Unmapped
 * values pass through untouched (the backend drops unknowns with a warning
 * and falls back to the default types).
 */
function applyContactTypeMapping(records, typeValueMap) {
  if (!typeValueMap || Object.keys(typeValueMap).length === 0) return records;
  (records || []).forEach(record => {
    if (!record.contactTypes) return;
    const mapped = String(record.contactTypes)
      .split(',').map(v => v.trim()).filter(Boolean)
      .map(v => typeValueMap[v.toLowerCase()] || v);
    record.contactTypes = [...new Set(mapped)].join(',');
  });
  return records;
}

/**
 * Mirror of the importDeals stage maps: resolves a raw CSV stage against the
 * configured stages by slug or label (case-insensitive, slug fallback).
 * Returns null when unresolved — updates never fall back to lead-new.
 * @param {Array<{value, label}>} stageRows - crm_deal_stages (from settings)
 */
function buildStageResolver(stageRows) {
  const stageByKey = new Map();
  (stageRows || []).forEach(s => {
    if (s.value) stageByKey.set(String(s.value).toLowerCase().trim(), s.value);
    if (s.label) stageByKey.set(String(s.label).toLowerCase().trim(), s.value);
  });
  return (rawStage) => {
    if (!rawStage || !String(rawStage).trim() || stageByKey.size === 0) return null;
    const key = String(rawStage).trim().toLowerCase();
    if (stageByKey.has(key)) return stageByKey.get(key);
    const slugKey = key.replace(/\s+/g, '-');
    if (stageByKey.has(slugKey)) return stageByKey.get(slugKey);
    return null;
  };
}

/**
 * Mirror of importMerge.buildContactMergeUpdate: the exact snake_case update
 * payload the backend would write for this record over this existing contact.
 *
 * @param {Object} record - mapped import record (camelCase)
 * @param {Object} current - existing crm_contacts row (snake_case)
 * @param {Object} [options] - { typeRows } crm_contact_types config
 * @returns {Object|null}
 */
function computeContactMergeUpdate(record, current, options = {}) {
  const typeResolver = buildTypeResolver(options.typeRows);
  const update = {};

  const setIfProvided = (column, value) => {
    if (provided(value)) update[column] = value;
  };

  setIfProvided('first_name', record.firstName);
  setIfProvided('last_name', record.lastName);
  setIfProvided('middle_initial', record.middleInitial);
  setIfProvided('company', record.company);
  setIfProvided('job_title', record.jobTitle);
  setIfProvided('preferred_contact_method', record.preferredContactMethod);
  setIfProvided('license_number', record.licenseNumber);
  setIfProvided('license_state', record.licenseState);
  setIfProvided('website', record.website);
  setIfProvided('office_address', record.officeAddress);
  setIfProvided('mailing_street', record.mailingStreet);
  setIfProvided('mailing_city', record.mailingCity);
  setIfProvided('mailing_state', record.mailingState);
  setIfProvided('mailing_zip', record.mailingZip);
  setIfProvided('source', record.source);

  const birthday = parseDateValue(record.birthday);
  if (birthday) update.birthday = birthday;

  const email = normalizeEmail(record.email);
  const phone = normalizePhone(record.phone);
  if (email) update.email = email;
  if (phone) update.phone = phone;
  const secondaryEmail = normalizeEmail(record.secondaryEmail);
  const secondaryPhone = normalizePhone(record.secondaryPhone);
  if (secondaryEmail) update.secondary_email = secondaryEmail;
  if (secondaryPhone) update.secondary_phone = secondaryPhone;

  if (provided(record.contactTypes)) {
    const csvTypes = typeResolver.hasConfig
      ? typeResolver.resolve(record.contactTypes)
      : parseList(record.contactTypes);
    if (csvTypes.length > 0) {
      const mergedTypes = unionList(current.contact_types, csvTypes);
      if (mergedTypes.length !== (current.contact_types || []).length) {
        update.contact_types = mergedTypes;
      }
      if (!current.primary_contact_type) {
        update.primary_contact_type = mergedTypes[0];
      }
    }
  }

  const csvTags = parseList(record.tags);
  if (csvTags.length > 0) {
    const mergedTags = unionList(current.tags, csvTags);
    if (mergedTags.length !== (current.tags || []).length) {
      update.tags = mergedTags;
    }
  }

  if (record.extraFields && Object.keys(record.extraFields).length > 0) {
    const currentExtras = current.extra_fields || {};
    const mergedExtras = { ...currentExtras, ...record.extraFields };
    if (JSON.stringify(mergedExtras) !== JSON.stringify(currentExtras)) {
      update.extra_fields = mergedExtras;
    }
  }

  return Object.keys(update).length > 0 ? update : null;
}

/**
 * Mirror of the matched-contact merge in importDeals: identical to a contact
 * merge except tags are excluded (they flow through crm_tag_apply on the deal).
 */
function computeDealContactMergeUpdate(record, current, options = {}) {
  const { tags: _dealTags, ...contactRecord } = record;
  return computeContactMergeUpdate(contactRecord, current, options);
}

/**
 * Mirror of the candidates object built for deal updates in importDeals.
 * @param {Object} record - mapped deal import record (camelCase)
 * @param {Object} [options] - { resolveStage } from buildStageResolver
 */
function buildDealCandidates(record, options = {}) {
  const resolveStage = options.resolveStage || (() => null);
  return {
    stage: resolveStage(record.stage),
    source_medium: record.source || null,
    product: record.product || null,
    amount: parseCurrencyValue(record.amount) ?? parseCurrencyValue(record.loanAmount),
    estimated_closing_date: parseDateValue(record.estimatedClosingDate || record.estCloseDate),
    app_date: parseDateValue(record.appDate),
    arive_id: record.ariveId || null,
    property_street: record.propertyAddress || null,
    property_city: record.propertyCity || record.city || null,
    property_state: record.propertyState || record.state || null,
    property_zip: record.propertyZip || record.zip || null,
    property_type: record.propertyType || null,
    purchase_or_refi: record.purchaseOrRefi || null,
    loan_purpose: record.loanPurpose || null,
    credit_score: parseIntValue(record.creditScore),
    loan_number: record.loanNumber || null,
    loan_term: parseIntValue(record.loanTerm),
    interest_rate: parseCurrencyValue(record.interestRate),
    loan_amount: parseCurrencyValue(record.loanAmount),
    lender: record.lender || null,
    occupancy_type: record.occupancyType || null,
    down_payment: parseCurrencyValue(record.downPayment),
    buyer_agent_name: record.buyerAgentName || null,
    buyer_agent_phone: record.buyerAgentPhone || null,
    buyer_agent_email: record.buyerAgentEmail || null,
    buyer_agent_company: record.buyerAgentCompany || null,
    seller_agent_name: record.sellerAgentName || null,
    seller_agent_phone: record.sellerAgentPhone || null,
    seller_agent_email: record.sellerAgentEmail || null,
    seller_agent_company: record.sellerAgentCompany || null,
    title_company_name: record.titleCompanyName || null,
    title_company_phone: record.titleCompanyPhone || null,
    title_company_email: record.titleCompanyEmail || null,
    title_company_contact: record.titleCompanyContact || null,
    extra_fields: record.extraFields || null,
  };
}

/**
 * Mirror of importMerge.buildDealMergeUpdate applied to buildDealCandidates:
 * the exact update payload the backend would write for this deal record.
 */
function computeDealMergeUpdate(record, currentDeal, options = {}) {
  const candidates = buildDealCandidates(record, options);
  const update = {};

  Object.entries(candidates).forEach(([column, value]) => {
    if (column === 'extra_fields') return;
    if (value === undefined || value === null) return;
    if (typeof value === 'string' && value.trim() === '') return;
    update[column] = value;
  });

  const extras = candidates.extra_fields;
  if (extras && Object.keys(extras).length > 0) {
    const currentExtras = (currentDeal && currentDeal.extra_fields) || {};
    const mergedExtras = { ...currentExtras, ...extras };
    if (JSON.stringify(mergedExtras) !== JSON.stringify(currentExtras)) {
      update.extra_fields = mergedExtras;
    }
  }

  return Object.keys(update).length > 0 ? update : null;
}

/**
 * Turn a merge update payload into a display-friendly diff list.
 * @returns {Array<{column, from, to}>}
 */
function diffFromUpdate(update, current) {
  return Object.entries(update || {}).map(([column, to]) => ({
    column,
    from: current && current[column] !== undefined ? current[column] : null,
    to,
  }));
}

// -------------------------------------------------------- duplicate detection

const CONTACT_PREVIEW_COLUMNS = [
  'id', 'owner_id', 'first_name', 'last_name', 'email', 'phone', 'company',
  'job_title', 'middle_initial', 'secondary_email', 'secondary_phone',
  'preferred_contact_method', 'license_number', 'license_state', 'website',
  'office_address', 'birthday', 'mailing_street', 'mailing_city',
  'mailing_state', 'mailing_zip', 'contact_types', 'primary_contact_type',
  'tags', 'source', 'extra_fields',
].join(', ');

const DEAL_PREVIEW_COLUMNS = [
  'id', 'contact_id', 'owner_id', 'deal_name', 'stage', 'loan_number',
  'amount', 'product', 'source_medium', 'loan_amount', 'loan_term',
  'interest_rate', 'lender', 'loan_purpose', 'purchase_or_refi',
  'credit_score', 'down_payment', 'occupancy_type', 'estimated_closing_date',
  'app_date', 'property_street', 'property_city', 'property_state',
  'property_zip', 'property_type', 'buyer_agent_name', 'buyer_agent_phone',
  'buyer_agent_email', 'buyer_agent_company', 'seller_agent_name',
  'seller_agent_phone', 'seller_agent_email', 'seller_agent_company',
  'title_company_name', 'title_company_phone', 'title_company_email',
  'title_company_contact', 'extra_fields',
].join(', ');

function chunks(list, size) {
  const out = [];
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
  return out;
}

/**
 * Find existing contacts matching the mapped records, by email (case
 * insensitive) or phone (raw + normalized) — the same scoping the backend
 * uses in batchCheckDuplicates / batchCheckCrossOwnerDuplicates.
 *
 * @param {Object} supabase - authenticated client (RLS applies; Studio is admin-only)
 * @param {Array} records - mapped import records (camelCase)
 * @param {string} ownerId - the import's target owner
 * @returns {Promise<Array>} aligned with records: null | {
 *   contact, matchedOn: 'email'|'phone', isCrossOwner, inFileRepeat }
 */
async function findContactDuplicates(supabase, records, ownerId) {
  const results = new Array(records.length).fill(null);
  if (!records || records.length === 0) return results;

  const uniqueEmails = [...new Set(
    records.map(r => normalizeEmail(r.email)).filter(Boolean)
  )];
  // Query both normalized and raw phone so legacy DB rows stored with
  // formatting are found — mirrors batchCheckCrossOwnerDuplicates.
  const uniquePhones = [...new Set(
    records
      .flatMap(r => [normalizePhone(r.phone) || null, r.phone ? String(r.phone).trim() : null])
      .filter(Boolean)
  )];

  const emailMap = new Map(); // normalized email -> [rows]
  const phoneMap = new Map(); // normalized phone -> [rows]

  for (const emailChunk of chunks(uniqueEmails, 100)) {
    const emailFilters = emailChunk.map(e => `email.ilike.${e}`).join(',');
    const { data, error } = await supabase
      .from('crm_contacts')
      .select(CONTACT_PREVIEW_COLUMNS)
      .or(emailFilters);
    if (error) { console.error('Preview email duplicate query failed:', error); continue; }
    (data || []).forEach(row => {
      if (!row.email) return;
      const key = row.email.toLowerCase().trim();
      if (!emailMap.has(key)) emailMap.set(key, []);
      emailMap.get(key).push(row);
    });
  }

  for (const phoneChunk of chunks(uniquePhones, 100)) {
    const { data, error } = await supabase
      .from('crm_contacts')
      .select(CONTACT_PREVIEW_COLUMNS)
      .in('phone', phoneChunk);
    if (error) { console.error('Preview phone duplicate query failed:', error); continue; }
    (data || []).forEach(row => {
      const key = normalizePhone(row.phone);
      if (!key) return;
      if (!phoneMap.has(key)) phoneMap.set(key, []);
      phoneMap.get(key).push(row);
    });
  }

  // Same-owner matches are preferred (they're the updatable ones); the
  // backend's blocking duplicates map is owner-scoped.
  const pick = (rows) => {
    if (!rows || rows.length === 0) return null;
    return rows.find(r => r.owner_id === ownerId) || rows[0];
  };

  const seenEmails = new Set();
  const seenPhones = new Set();

  records.forEach((record, idx) => {
    const email = normalizeEmail(record.email);
    const phone = normalizePhone(record.phone);
    if (!email && !phone) return;

    // Mirrors importContacts: in-file repeats are checked first and always skip
    if ((email && seenEmails.has(email)) || (phone && seenPhones.has(phone))) {
      results[idx] = { contact: null, matchedOn: null, isCrossOwner: false, inFileRepeat: true };
      return;
    }
    if (email) seenEmails.add(email);
    if (phone) seenPhones.add(phone);

    // Email match takes priority over phone, like the backend
    const emailHit = email ? pick(emailMap.get(email)) : null;
    const phoneHit = phone ? pick(phoneMap.get(phone)) : null;
    const hit = emailHit || phoneHit;
    if (!hit) return;

    results[idx] = {
      contact: hit,
      matchedOn: emailHit ? 'email' : 'phone',
      isCrossOwner: hit.owner_id !== ownerId,
      inFileRepeat: false,
    };
  });

  return results;
}

/** Mirror of importDeals.buildDealDedupeKeys. */
function buildDealDedupeKeys(contactId, loanNumber, amount, product) {
  const keys = [];
  const ln = loanNumber ? String(loanNumber).trim().toLowerCase() : '';
  if (ln) {
    keys.push(`${contactId}|ln:${ln}`);
  }
  const amt = amount === null || amount === undefined ? '' : String(Number(amount));
  const prod = product ? String(product).trim().toLowerCase() : '';
  if (amt !== '' || prod !== '') {
    keys.push(`${contactId}|ap:${amt}|${prod}`);
  }
  if (keys.length === 0) {
    keys.push(`${contactId}|bare`);
  }
  return keys;
}

/**
 * Find existing deals the mapped deal records would collide with — same
 * contact + loan number, same contact + amount/product, or (bare rows) any
 * previous import-created deal for the contact. Mirrors importDeals pass 2,
 * including in-file repeat reservation.
 *
 * @returns {Promise<{ dealMatches: Array, contactMatches: Array }>}
 *   dealMatches aligned with records: null | {
 *     deal, matchedOn: 'loan-number'|'amount-product'|'previous-import',
 *     isCrossOwner, inFileRepeat }
 */
async function findDealDuplicates(supabase, records, ownerId) {
  const contactMatches = await findContactDuplicates(supabase, records, ownerId);
  const dealMatches = new Array(records.length).fill(null);

  // Only same-owner contact matches can host duplicate deals: the backend's
  // contactDuplicates map is owner-scoped, so rows matching a cross-owner
  // contact create a NEW contact + deal for this owner instead.
  const contactIds = [...new Set(
    contactMatches
      .filter(m => m && m.contact && !m.isCrossOwner)
      .map(m => m.contact.id)
  )];

  const keyToDeal = new Map();
  const importDealByContact = new Map();
  for (const idChunk of chunks(contactIds, 100)) {
    const { data, error } = await supabase
      .from('crm_deals')
      .select(DEAL_PREVIEW_COLUMNS)
      .in('contact_id', idChunk);
    if (error) { console.error('Preview deal duplicate query failed:', error); continue; }
    (data || []).forEach(deal => {
      buildDealDedupeKeys(deal.contact_id, deal.loan_number, deal.amount, deal.product)
        .forEach(key => { if (!keyToDeal.has(key)) keyToDeal.set(key, deal); });
      if (deal.source_medium === 'import' && !importDealByContact.has(deal.contact_id)) {
        importDealByContact.set(deal.contact_id, deal);
      }
    });
  }

  const reservedKeys = new Set();
  const reservedBareContacts = new Set();
  // email/phone key -> resolved contact id (real or pseudo). Unlike a contacts
  // import, the deals backend never skips in-file contact repeats — every row
  // with the same email/phone resolves to the SAME contact — so repeats must
  // inherit the first occurrence's resolution for dedupe keys to collide.
  const resolvedByKey = new Map();

  records.forEach((record, idx) => {
    if (!record.firstName || !record.lastName || (!record.email && !record.phone)) return;

    const match = contactMatches[idx];
    const emailKey = normalizeEmail(record.email);
    const phoneKey = normalizePhone(record.phone);
    // Rows for contacts created by this same file get a pseudo-id so in-file
    // duplicate deals between them are still detected (the backend resolves
    // them to the same created contact id).
    let contactId;
    if (match && match.contact && !match.isCrossOwner) {
      contactId = match.contact.id;
    } else {
      contactId = (emailKey && resolvedByKey.get(`e:${emailKey}`)) ||
        (phoneKey && resolvedByKey.get(`p:${phoneKey}`)) ||
        `new:${emailKey || phoneKey}`;
    }
    if (emailKey && !resolvedByKey.has(`e:${emailKey}`)) resolvedByKey.set(`e:${emailKey}`, contactId);
    if (phoneKey && !resolvedByKey.has(`p:${phoneKey}`)) resolvedByKey.set(`p:${phoneKey}`, contactId);

    const parsedAmount = parseCurrencyValue(record.amount) ?? parseCurrencyValue(record.loanAmount);
    const keys = buildDealDedupeKeys(contactId, record.loanNumber, parsedAmount, record.product);
    const isBareRow = keys.length === 1 && keys[0].endsWith('|bare');

    let deal = null;
    for (const key of keys) {
      const hit = keyToDeal.get(key);
      if (hit) { deal = hit; break; }
    }
    if (!deal && isBareRow && importDealByContact.has(contactId)) {
      deal = importDealByContact.get(contactId);
    }

    if (deal) {
      dealMatches[idx] = {
        deal,
        matchedOn: record.loanNumber ? 'loan-number' : (!isBareRow ? 'amount-product' : 'previous-import'),
        isCrossOwner: !!(deal.owner_id && deal.owner_id !== ownerId),
        inFileRepeat: false,
      };
      return;
    }

    if (keys.some(k => reservedKeys.has(k)) || (isBareRow && reservedBareContacts.has(contactId))) {
      dealMatches[idx] = { deal: null, matchedOn: 'in-file', isCrossOwner: false, inFileRepeat: true };
      return;
    }

    keys.forEach(k => reservedKeys.add(k));
    if (isBareRow) reservedBareContacts.add(contactId);
  });

  return { dealMatches, contactMatches };
}

module.exports = {
  normalizeEmail,
  normalizePhone,
  parseCurrencyValue,
  parseIntValue,
  parseDateValue,
  provided,
  parseList,
  unionList,
  buildTypeResolver,
  resolveContactTypeValue,
  collectContactTypeValues,
  applyContactTypeMapping,
  buildStageResolver,
  computeContactMergeUpdate,
  computeDealContactMergeUpdate,
  buildDealCandidates,
  computeDealMergeUpdate,
  diffFromUpdate,
  buildDealDedupeKeys,
  findContactDuplicates,
  findDealDuplicates,
  CONTACT_PREVIEW_COLUMNS,
  DEAL_PREVIEW_COLUMNS,
};
